import React, { useState, useEffect, useCallback } from 'react';
import { useRealtimeEvent } from '../../contexts/RealtimeContext';
import api from '../../lib/api';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Bell, RefreshCw, AlertTriangle, AlertOctagon, Package } from 'lucide-react';

export default function StockAlertsPage() {
  const [data, setData] = useState({ alerts: [], total: 0, critical: 0, warning: 0 });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/stock-alerts');
      setData(res.data);
    } catch { toast.error('Eroare la incarcarea alertelor'); }
    finally { setLoading(false); }
  }, []);

  // Real-time: refresh on stock changes
  useRealtimeEvent('stock_change', fetchData);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filtered = data.alerts.filter(a => filter === 'all' || a.level === filter);

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <RefreshCw className="w-6 h-6 animate-spin text-red-500" />
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-3" data-testid="alerts-title">
            <Bell className="w-7 h-7 text-red-500" /> Alerte Stoc
          </h1>
          <p className="text-sm text-slate-500 mt-1">Pozitii BIN sub stocul minim configurat</p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchData}><RefreshCw className="w-4 h-4 mr-1" /> Actualizeaza</Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3">
        {[
          { key: 'all', label: 'Total Alerte', val: data.total, cls: 'bg-slate-50 border-slate-200 text-slate-700' },
          { key: 'CRITICAL', label: 'Critice (stoc 0)', val: data.critical, cls: 'bg-red-50 border-red-200 text-red-700' },
          { key: 'WARNING', label: 'Atentionare', val: data.warning, cls: 'bg-amber-50 border-amber-200 text-amber-700' },
        ].map(s => (
          <button key={s.key} onClick={() => setFilter(filter === s.key ? 'all' : s.key)}
            className={`p-4 rounded-lg border text-left transition-all ${s.cls} ${filter === s.key ? 'ring-2 ring-red-400' : ''}`}
            data-testid={`alert-stat-${s.key}`}>
            <div className="text-3xl font-bold">{s.val}</div>
            <div className="text-xs font-medium mt-0.5">{s.label}</div>
          </button>
        ))}
      </div>

      {/* Alerts List */}
      {filtered.length === 0 ? (
        <div className="bg-emerald-50 rounded-xl border border-emerald-200 p-8 text-center">
          <Package className="w-10 h-10 text-emerald-400 mx-auto mb-3" />
          <p className="text-emerald-700 font-medium">Nicio alerta activa!</p>
          <p className="text-sm text-emerald-600 mt-1">Toate pozitiile sunt peste stocul minim configurat.</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
          <table className="w-full text-sm" data-testid="alerts-table">
            <thead>
              <tr className="bg-slate-50 border-b">
                <th className="p-3 text-left text-xs font-semibold text-slate-500">Nivel</th>
                <th className="p-3 text-left text-xs font-semibold text-slate-500">Pozitie BIN</th>
                <th className="p-3 text-left text-xs font-semibold text-slate-500">Dulap</th>
                <th className="p-3 text-left text-xs font-semibold text-slate-500">Raft</th>
                <th className="p-3 text-left text-xs font-semibold text-slate-500">Stoc Actual</th>
                <th className="p-3 text-left text-xs font-semibold text-slate-500">Stoc Minim</th>
                <th className="p-3 text-left text-xs font-semibold text-slate-500">Deficit</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(a => (
                <tr key={a.bin_id} className={`border-b ${a.level === 'CRITICAL' ? 'bg-red-50/50' : ''}`} data-testid={`alert-${a.code}`}>
                  <td className="p-3">
                    {a.level === 'CRITICAL'
                      ? <Badge className="bg-red-500/10 text-red-600 border-red-200 border text-xs gap-1"><AlertOctagon className="w-3 h-3" /> Critic</Badge>
                      : <Badge className="bg-amber-500/10 text-amber-600 border-amber-200 border text-xs gap-1"><AlertTriangle className="w-3 h-3" /> Atentie</Badge>
                    }
                  </td>
                  <td className="p-3 font-mono font-medium text-slate-800">{a.code}</td>
                  <td className="p-3 text-slate-600">{a.cabinet || '-'}</td>
                  <td className="p-3 text-slate-600">{a.shelf || '-'}</td>
                  <td className="p-3">
                    <span className={`font-bold font-mono ${a.current_qty === 0 ? 'text-red-600' : 'text-amber-600'}`}>{a.current_qty}</span>
                  </td>
                  <td className="p-3 font-mono text-slate-500">{a.min_stock}</td>
                  <td className="p-3">
                    <span className="font-bold font-mono text-red-600">-{a.deficit}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
